// 帖子相关的 Action 层，负责业务逻辑和异步操作
import { action, makeObservable, observable, runInAction } from 'mobx';
import { ThreadStore } from './store';
import { readThreadList, readThreadDetail, readPostList, getSinglePost, createUserslikes, threadOpera, threadShare, reports, postsCreate, votesOptions, threadFavorite, postsUpdate, createFollow, followDelete, readPostComments, readIndexRecommendThread, readThreads, submitForm, readCommentDetail, submitFeedbackSame, readFeedbacks, readFeedbackProblems, readFeedbackSolvedCount, readProductCategories, readProducts, submitFeedbackConfirm } from '@server';
import Toast from '@components/Toast';
import updateImageSrc from '@utils/update-image-src'

export class ThreadAction extends ThreadStore {

    constructor(initData = {}) {
        super(initData);  // 传递正确的参数
        makeObservable(this);
    }

    /**
     * 获取帖子列表（异步）
     * 统一管理 loading、错误、分页、hasMore 等状态
     * 页面和 list 组件可直接响应 listStore 的状态
     * @param {object} params 查询参数
     *   - params.page[number]: 页码
     *   - params.page[size]: 每页数量
     *   - params.filter: 过滤条件（可选）
     * @returns {Promise<void>}
     */
    @action
    async getThreadList(params) {
        // 1. 标记 loading 状态
        runInAction(() => {
            this.listStore.setAttribute({ namespace: 'threadList', key: 'loading', value: true });
        });
        // 2. 发起异步请求，获取帖子列表
        const res = await readThreadList(params);
        runInAction(() => {
            if (res.code === 0) {
                const setListParams = {
                    namespace: 'threadList',
                    data: { data: res.data },
                    page: params['page[number]'],
                    path: 'data.data',
                    search: { filter: params.filter || {} },
                    hasMore: res.data.page ? !!res.data.page.hasMore : false
                };
                // 写入列表数据
                this.listStore.setList(setListParams);
                // 记录分页信息
                this.listStore.setAttribute({ namespace: 'threadList', key: 'currentPage', value: params['page[number]'] });
                this.listStore.setAttribute({ namespace: 'threadList', key: 'page', value: params['page[number]'] });
                this.listStore.setAttribute({ namespace: 'threadList', key: 'pageSize', value: params['page[size]'] || 20 });
                // 清除错误
                this.listStore.setAttribute({ namespace: 'threadList', key: 'requestError', value: { isError: false, errorText: '' } });
            } else {
                this.listStore.setAttribute({ namespace: 'threadList', key: 'requestError', value: { isError: true, errorText: res.message || '加载失败' } });
            }
            // 关闭 loading
            this.listStore.setAttribute({ namespace: 'threadList', key: 'loading', value: false });
        });
    }

    /**
     * 获取推荐帖子列表
     * @param {object} params 查询参数
     *   - params.page: 页码
     *   - params.perPage: 每页数量
     */
    @action
    async getIndexRecommendThread(params) {
        const res = await readIndexRecommendThread(params);
        return res;
    }

    /**
     * 获取帖子列表
     * @param {object} params 查询参数
     *   - params.page: 页码
     *   - params.perPage: 每页数量
     * @param {string} namespace 列表命名空间
     */
    @action
    async getThreads(params, namespace = 'searchThreadList') {
        // 确定最终要使用的命名空间
        const targetNamespace = (namespace === 'searchAll' || namespace === 'searchThreadList') 
            ? 'searchThreadList' 
            : namespace;
        
        console.log(`getThreads: 接收到的 namespace=${namespace}, 实际使用的 namespace=${targetNamespace}`);
        
        // 1. 标记 loading 状态
        runInAction(() => {
            this.listStore.setAttribute({ 
                namespace: targetNamespace, 
                key: 'loading', 
                value: true 
            });
        });
        
        // 2. 发起异步请求，获取帖子列表
        const res = await readThreads(params);
        console.log(res, '帖子结果');
        
        runInAction(() => {
            if (res.code === 0) {
                const setListParams = {
                    namespace: targetNamespace,
                    data: { data: res.data },
                    page: params['page[number]'] || 1,
                    path: 'data.data',
                    search: { filter: params.filter || {} },
                    hasMore: res.data.page ? !!res.data.page.hasMore : false
                };
                
                console.log(`准备写入数据到列表: ${targetNamespace}`, setListParams);
                
                // 写入列表数据
                this.listStore.setList(setListParams);
                
                // 记录分页信息
                this.listStore.setAttribute({ 
                    namespace: targetNamespace, 
                    key: 'currentPage', 
                    value: params['page[number]'] || 1 
                });
                this.listStore.setAttribute({ 
                    namespace: targetNamespace, 
                    key: 'page', 
                    value: params['page[number]'] || 1 
                });
                this.listStore.setAttribute({ 
                    namespace: targetNamespace, 
                    key: 'pageSize', 
                    value: params['page[size]'] || 20 
                });
                
                // 清除错误
                this.listStore.setAttribute({ 
                    namespace: targetNamespace, 
                    key: 'requestError', 
                    value: { isError: false, errorText: '' } 
                });
                
                // 调试：查看写入后的列表状态
                console.log(`写入后 ${targetNamespace} 列表状态:`, this.listStore.lists[targetNamespace]);
            } else {
                this.listStore.setAttribute({ 
                    namespace: targetNamespace, 
                    key: 'requestError', 
                    value: { isError: true, errorText: res.message || '加载失败' } 
                });
            }
            
            // 关闭 loading
            this.listStore.setAttribute({ 
                namespace: targetNamespace, 
                key: 'loading', 
                value: false 
            });
        });
        
        return res;
    }
    
    

    /**
     * 直接设置 threadList（通常不推荐，建议用 listStore 统一管理）
     * @param {array} threadList 帖子列表
     */
    @action
    setThreadList(threadList) {
        this.threadList = threadList;
    }

    /**
     * 直接设置 threadList（通常不推荐，建议用 listStore 统一管理）
     * @param {array} threadList 帖子列表
     */
    @action
    setSearchThreadList(searchThreadList) {
        this.searchThreadList = searchThreadList;
    }

    // 获取帖子评论列表
    @action
    async getPostList(params) {
        // 1. 标记 loading 状态，页面可直接响应
        this.listStore.setAttribute({
            namespace: 'postList',
            key: 'loading',
            value: true
        });
        // 2. 发起异步请求，获取帖子列表
        const res = await readPostList(params);
        const code = typeof res.code !== 'undefined' ? res.code : res.Code;
        const rawData = res.data || res.Data || {};
        const list = rawData.data || rawData.pageData || [];
        const pageInfo = rawData.page || rawData.Page || {};
        const currentPage = pageInfo.number || params['page[number]'] || params.page || 1;
        const totalPage = pageInfo.totalPage;
        const hasMore = typeof pageInfo.hasMore === 'boolean'
            ? pageInfo.hasMore
            : (typeof totalPage === 'number' ? currentPage < totalPage : false);

        runInAction(() => {
            if (code === 0) {
                this.listStore.setList({ namespace: 'postList', data: { data: list }, page: currentPage, path: 'data', hasMore });
                this.listStore.setAttribute({ namespace: 'postList', key: 'requestError', value: { isError: false, errorText: '' } });
            } else {
                this.listStore.setAttribute({ namespace: 'postList', key: 'requestError', value: { isError: true, errorText: res.message || res.Message || '加载失败' } });
            }
            this.listStore.setAttribute({ namespace: 'postList', key: 'loading', value: false });
        });
        return res;
    }

    // 获取单条评论
    @action
    async getPostDetail(postId) {
        const res = await getSinglePost(postId);
        return res;
    }

    @action
    prependPostToList(newComment) {
        if (!newComment) {
            console.error('[threadDetail][highlight] prepend aborted: empty newComment');
            return;
        }
        const namespace = 'postList';
        const list = this.listStore.lists[namespace];
        if (!list) {
            console.error('[threadDetail][highlight] prepend aborted: postList namespace missing');
            return;
        }
        const data = list.data || {};
        const pageKeys = Object.keys(data);
        const firstPageKey = pageKeys.length ? String(Math.min(...pageKeys.map(page => Number(page)))) : '1';
        if (!data[firstPageKey]) {
            data[firstPageKey] = observable([]);
        }
        const pageData = data[firstPageKey];
        if (!Array.isArray(pageData)) {
            console.error('[threadDetail][highlight] prepend aborted: first page data is not array', {
                firstPageKey,
                pageData,
            });
            return;
        }

        const newCommentId = newComment?.id || newComment?._id;
        console.error('[threadDetail][highlight] prepend before', {
            firstPageKey,
            newCommentId,
            pageSize: list.attribs?.pageSize || 20,
            firstPageCount: pageData.length,
            firstPageIds: pageData.slice(0, 10).map(item => item?.id || item?._id),
        });

        const existingIndex = pageData.findIndex(item => (item?.id || item?._id) === newCommentId);
        if (existingIndex !== -1) {
            console.error('[threadDetail][highlight] prepend removing duplicate', {
                newCommentId,
                existingIndex,
            });
            pageData.splice(existingIndex, 1);
        }

        pageData.unshift(newComment);

        const pageSize = list.attribs?.pageSize || 20;
        if (pageData.length > pageSize) {
            pageData.splice(pageSize);
        }
        this.listStore.lists = { ...this.listStore.lists };

        console.error('[threadDetail][highlight] prepend after', {
            newCommentId,
            firstItemId: pageData[0]?.id || pageData[0]?._id,
            firstPageCount: pageData.length,
            firstPageIds: pageData.slice(0, 10).map(item => item?.id || item?._id),
        });
    }

    @action
    appendReplyToPost({ mainPostId, newComment }) {
        if (!mainPostId || !newComment) return;
        const namespace = 'postList';
        const list = this.listStore.lists[namespace];
        if (!list) return;
        const { data } = list;
        let updated = false;
        Object.keys(data || {}).forEach((pageKey) => {
            const pageData = data[pageKey];
            if (!Array.isArray(pageData)) return;
            const targetIndex = pageData.findIndex(item => item?.id === mainPostId || item?._id === mainPostId);
            if (targetIndex === -1) return;
            const item = pageData[targetIndex] || {};
            const lastThree = Array.isArray(item.lastThreeComments) ? item.lastThreeComments.slice() : [];
            const nextLastThree = [...lastThree, newComment];
            const limitedLastThree = nextLastThree.slice(-3);
            pageData[targetIndex] = {
                ...item,
                replyCount: (item.replyCount || 0) + 1,
                lastThreeComments: limitedLastThree,
            };
            updated = true;
        });
        if (updated) {
            this.listStore.lists = { ...this.listStore.lists };
        }
    }

    /**
     * 直接设置 postList（通常不推荐，建议用 listStore 统一管理）
     * @param {array} postList 帖子评论列表
     */
    @action
    setPostList(postList) {
        this.postList = postList;
    }

    /**
   * 获取帖子详细信息
   * @param {number} id 帖子id
   * @returns 帖子详细信息
   */
    @action
    async getThreadDetail(id, params = {}) {
        try {
            const ret = await readThreadDetail(id, params);
            if (ret.code === 0) {
                ret.data.content = updateImageSrc(ret.data.content, 1); // 给详情内容中的图片加缩略图参数
                this.setThreadData(ret.data);
            } else if (ret.code === -16022) {
                this.setThreadData({code: ret.code});
                Toast.showToast({ title: ret.message || '帖子不存在或已删除', icon: 'error' });
            } else {
                Toast.showToast({ title: ret.message || '加载失败，请重试', icon: 'error' });
            }
            return ret;
        } catch (error) {
            Toast.showToast({ title: '加载失败，请重试', icon: 'error' });
            throw error;
        }
    }

     /**
   * 获取某个评论详细信息
   * @returns 评论详细信息
   */
    @action
    async getCommentDetail(params = {}) {
        try {
            const ret = await readCommentDetail(params);
            if (ret.code === 0) { 
                return ret
            }
        } catch (error) {
            // Toast.showToast({ title: '加载失败，请重试', icon: 'error' });
            throw error;
        }
    }
    

    // 我也有相同问题
    async submitFeedbackSame(id, params) {
        const res = await submitFeedbackSame(id, params);
        if (res.code == 0) {
            runInAction(() => {
                if (this.threadData) {
                    this.threadData = {
                        ...this.threadData,
                        feedback: {
                            ...this.threadData.feedback,
                            isSame: !this.threadData.feedback?.isSame
                        }
                    };
                }
            });
        }
        return res
    }
    
    /**
   * 帖子置顶、取消置顶，沉帖，精华，屏蔽，推荐，删除
   * @param {number} id 被赞主体id
   * @param {string} isSticky 0 取消置顶 1 分类置顶 2全局置顶
   * @param {boolean} showIndex 是否显示在首页 true 显示 false不显示
   * @param {string} showIndexAt 显示在首页截止日期
   * @param {string} stickyAt 0 置顶截止日期
   * @param { boolean } isSink 是否下沉
   * @returns 帖子详细信息
   */
    @action
    async createThreadOpera(params) {
        const ret = await threadOpera(params);
        if(ret.code == 0) {

        }
        return ret;
    }


    // 立即更新点赞状态（用于UI反馈）
    @action
    updateLikeStatus(isLiked) {
        if (this.threadData) {
            this.threadData.isLiked = isLiked;
        }
    }

    // 立即更新收藏状态（用于UI反馈）
    @action
    updateFavoriteStatus(isFavorite) {
        if (this.threadData) {
            this.threadData.isFavorite = isFavorite;
        }
    }

    // 帖子收藏
    async threadFavorite(params) {
        const ret = await threadFavorite(params);
        console.log('threadFavorite 返回结果:', ret, '当前 threadData:', this.threadData?.id || this.threadData?.threadId);
        if(ret.code == 0) {
            // 在 runInAction 中更新状态，确保符合 strict 模式要求
            runInAction(() => {
                const currentThreadId = this.threadData?.id || this.threadData?.threadId;
                console.log('收藏 id 匹配检查:', { currentThreadId, paramId: params.id, match: currentThreadId === params.id });
                
                if (this.threadData && currentThreadId === params.id) {
                    // 如果API返回了favoriteCount，使用API的数据；否则手动计算
                    let newFavoriteCount = this.threadData.favoriteCount || 0;
                    
                    if (typeof ret.data?.favoriteCount === 'number') {
                        newFavoriteCount = ret.data.favoriteCount;
                    } else {
                        // API未返回favoriteCount时，手动计算
                        // params.isFavorite 为 true 表示收藏，false 表示取消收藏
                        // 由于 updateFavoriteStatus 已经改变了状态，我们直接根据 params.isFavorite 计算
                        if (params.isFavorite) {
                            // 收藏操作：数量 +1
                            newFavoriteCount = (this.threadData.favoriteCount || 0) + 1;
                        } else {
                            // 取消收藏操作：数量 -1
                            newFavoriteCount = Math.max(0, (this.threadData.favoriteCount || 0) - 1);
                        }
                    }
                    
                    console.log('收藏数量计算:', { 
                        旧数量: this.threadData.favoriteCount, 
                        新数量: newFavoriteCount,
                        操作: params.isFavorite ? '收藏' : '取消收藏'
                    });
                    
                    this.threadData = {
                        ...this.threadData,
                        isFavorite: params.isFavorite,
                        favoriteCount: newFavoriteCount
                    };
                }
            });
        }
        return ret;
    }

    // 关注用户
    @action
    async createFollowUser(params) {
        const res = await createFollow(params);
        if(res.code == 0) {
            // 更新 threadData 中的关注状态
            runInAction(() => {
                if (this.threadData && this.threadData.user && this.threadData.user.code === params.toUserCode) {
                    this.threadData = {
                        ...this.threadData,
                        user: {
                            ...this.threadData.user,
                            isFollow: true
                        }
                    };
                }
            });
        }
        return res
    }

    // 取消关注用户或者移除粉丝
    @action
    async followDelete(params) {
        const res = await followDelete(params);
        if(res.code == 0) {
            // 更新 threadData 中的关注状态
            runInAction(() => {
                if (this.threadData && this.threadData.user && this.threadData.user.code === params.code) {
                    this.threadData = {
                        ...this.threadData,
                        user: {
                            ...this.threadData.user,
                            isFollow: false
                        }
                    };
                }
            });
        }
        return res
    }

    // 分享帖子
    async createThreadShare(params) {
        const ret = await threadShare(params);
        if (ret.code == 0) {
            // 更新 threadData 中的分享数
            runInAction(() => {
                this.threadData = {
                    ...this.threadData,
                    shareCount: (this.threadData?.shareCount ?? 0) + 1
                };
            });
        }
        return ret;
    }

    /**
     * 通用更新帖子在所有列表中的状态
     * @param {object} params 参数
     * @param {object} updates 要更新的字段
     */
    @action
    updateThreadInAllLists(params, updates, ret) {
        const { id, isLiked, likeCount } = params;
        
        // 准备更新数据
        const updateData = {
            isLiked: isLiked,
            likeCount: isLiked ? likeCount + 1 : likeCount - 1
        };
        
        // // 确定 likeCount
        // if (updates?.likeCount !== undefined) {
        //     updateData.likeCount = updates.likeCount;
        // } else if (ret?.data?.likeCount !== undefined) {
        //     updateData.likeCount = likeCount;
        // }
        
        console.log('更新数据---11---222--33:', { id, updateData });
        
        // 动态扫描当前 listStore 中所有已注册的命名空间
        // 跨 store 的列表（如 topic 的 topicThreadList）由各 store 自行负责更新，不在此处硬编码
        const threadNamespaces = Object.keys(this.listStore?.lists || {});

        // 更新所有列表
        threadNamespaces.forEach(namespace => {
            // 查找并更新
            let updated = this.listStore.updateListItem({
                namespace: namespace,
                id: id,
                updates: updateData
            });
            
            if (updated) {
                console.log(`✅ 更新 ${namespace} 成功`);
            }
        });
        
        // // 更新帖子详情
        // if (this.threadData && (this.threadData.id === id || this.threadData.threadId === id)) {
        //     this.threadData = {
        //         ...this.threadData,
        //         ...updateData
        //     };
        // }
    }

    /**
   * 点赞帖子、评论
   * @param {number} id 被赞主体id
   * @param {string} type 类型（帖子 thread 评论 post）
   * @param { boolean } isLiked 是否点赞
   * @returns 帖子详细信息
   */
    async createUserslikes(params, namespace = 'threadList') {
        console.log( params, '参数参数参数')
        const ret = await createUserslikes(params);
        console.log('createUserslikes API 返回---11:', ret);
        if(ret.code == 0) {
            // 在 runInAction 中更新状态，确保符合 strict 模式要求
            runInAction(() => {
                // 接口成功后，只更新服务器返回的数据，避免覆盖本地已更新的数据
                if (params.type === 'thread') {
                    // 构建更新对象，只包含服务器返回的有效数据
                    const updates = {
                        isLiked: params.isLiked,
                    };
                    if (typeof params?.likeCount === 'number') {
                        updates.likeCount = params.likeCount;
                    }
                    // 使用通用方法更新所有列表
                    this.updateThreadInAllLists(params, updates, ret);
                    
                    // // 更新 threadList
                    // this.listStore.updateListItem({
                    //     namespace: 'threadList',
                    //     id: params.id,
                    //     updates
                    // });
                    
                    
                    // // 更新 searchThreadList
                    // this.listStore.updateListItem({
                    //     namespace: 'searchThreadList',
                    //     id: params.id,
                    //     updates
                    // });

                    if (this.threadData && (this.threadData.id === params.id || this.threadData.threadId === params.id)) {
                        this.threadData = {
                            ...this.threadData,
                            isLiked: !!params.isLiked,
                            likeCount: typeof ret.data?.likeCount === 'number'
                                ? ret.data.likeCount
                                : (typeof this.threadData.likeCount === 'number'
                                    ? (params.isLiked ? this.threadData.likeCount + 1 : Math.max(0, this.threadData.likeCount - 1))
                                    : ret.data?.likeCount)
                        };
                    }
                }
                // 更新评论列表
                else if (params.type === 'post') {
                    // 构建更新对象，只包含服务器返回的有效数据
                    const updates = {
                        isLiked: params.isLiked,
                    };
                    // 如果服务器返回了有效的 likeCount，才包含在更新中
                    if (typeof ret.data?.likeCount === 'number') {
                        updates.likeCount = ret.data.likeCount;
                    }
                    
                    const updated = this.listStore.updateListItem({          
                        namespace: 'postList',
                        id: params.id,
                        updates
                    });
                    // 如果没有找到对应的评论，打印当前列表数据用于调试
                    if (!updated) {
                        console.warn('未找到要更新的评论，当前 postList 数据:', this.listStore.lists['postList']);
                    }
                }
            });
        } else {
        }
        
        return ret;
    }

    /**
   * 举报帖子、评论
   * @param {number} id 被赞主体id
   * @param {string} type 类型（帖子 thread 评论 post）
   * @returns 帖子详细信息
   */
    @action
    async createReports(params) {
        const ret = await reports(params);
        if(ret.code == 0) {
            // 更新列表store
            // this.updateListStore();
        }
        
        return ret;
    }

    /**
   * 投票
   * @param {number} vote 选项id
   * @param {string} id 选项id合集
   * @returns 投票结果
   */
    @action
    async createVotesOptions(vote, params) {
        const ret = await votesOptions(vote, params);
        if(ret.code == 0) {
            
        }
        
        return ret;
    }
    

    // 打赏主题
    async postsCreate(params) {
        const ret = await postsCreate(params);
        if(ret.code == 0) {
           
        }
        
        return ret;
    }
    
    // 提交表单
    @action
    async createFormSubmit(params) {
        const { formId, signUpInfo, time } = params;
        if (!formId) {
            return { code: -1, message: '表单ID不存在' };
        }
        
        const data = {
            data: signUpInfo
        };
        
        const ret = await submitForm(formId, data);
        
        // 提交成功后更新 threadData 中的表单状态和参与人数
        if (ret?.Code === 0 || ret?.code === 0) {
            runInAction(() => {
                if (this.threadData && this.threadData.form) {
                    // 如果用户之前未提交过表单，现在提交成功，参与人数应该 +1
                    const wasNotSubmitted = !this.threadData.form.isSubmitted;
                    let newUserCount = this.threadData.form.userCount || 0;
                    
                    if (wasNotSubmitted) {
                        // 用户首次提交，参与人数 +1
                        newUserCount = newUserCount + 1;
                    } else if (typeof ret?.data?.userCount === 'number') {
                        // 用户修改已提交的表单，使用后端返回的最新 userCount
                        newUserCount = ret.data.userCount;
                    }
                    // 如果已提交且后端未返回 userCount，保持原有数值
                    
                    this.threadData = {
                        ...this.threadData,
                        form: {
                            ...this.threadData.form,
                            isSubmitted: true,  // 标记为已填写
                            userCount: newUserCount  // 更新参与人数
                        }
                    };
                }
            });
        }
        
        return ret;
    }
    
    // 修改回复（评论置顶、沉帖、删除）
    async postsUpdate(params) {
        const ret = await postsUpdate(params);
        
        return ret;
    }

    /**
     * 评论操作统一入口：删除/沉贴/置顶，并同步更新 listStore
     * @param {{ id: any, type: 0|1, isDeleted?: boolean, isSink?: boolean, isSticky?: boolean }} params
     */
    async updateCommentAndSyncList(params) {
        console.log(params.isDeleted, '是不是删除呀');
        const { id, type, isDeleted, isSink, isSticky } = params || {};
        const requestParams = { id };
        if (typeof isDeleted !== 'undefined') requestParams.isDeleted = isDeleted;
        if (typeof isSink !== 'undefined') requestParams.isSink = isSink;
        if (typeof isSticky !== 'undefined') requestParams.isSticky = isSticky;

        console.log('updateCommentAndSyncList 发送给接口的参数:', requestParams);

        const ret = await postsUpdate(requestParams);
        const code = typeof ret.code !== 'undefined' ? ret.code : ret.Code;
        if (code !== 0) return ret;

        // 使用 runInAction 包装状态修改
        runInAction(() => {
            const namespaces = ['postList', 'postlist'];
            let anyNamespaceUpdated = false;
            const applyUpdate = (ns) => {
                const listRef = this.listStore?.lists?.[ns];
                if (!listRef) return;
                const listDataRaw = listRef.data;
                const isArrayData = Array.isArray(listDataRaw);
                const pageKeys = isArrayData
                    ? []
                    : Object.keys(listDataRaw || {}).sort((a, b) => Number(a) - Number(b));
                const pageMap = isArrayData
                    ? null
                    : pageKeys.reduce((result, key) => {
                        result[key] = Array.isArray(listDataRaw[key]) ? [...listDataRaw[key]] : [];
                        return result;
                    }, {});
                const arrayData = isArrayData ? [...listDataRaw] : null;

                const findTopLevelItem = () => {
                    if (isArrayData) {
                        const index = arrayData.findIndex(item => item?.id === id || item?._id === id);
                        return index === -1 ? null : { key: null, index, item: arrayData[index] };
                    }
                    for (let i = 0; i < pageKeys.length; i++) {
                        const key = pageKeys[i];
                        const currentPage = pageMap[key] || [];
                        const index = currentPage.findIndex(item => item?.id === id || item?._id === id);
                        if (index !== -1) {
                            return { key, index, item: currentPage[index] };
                        }
                    }
                    return null;
                };

                const replaceTopLevelItem = (location, nextItem) => {
                    if (!location) return;
                    if (isArrayData) {
                        arrayData[location.index] = nextItem;
                        return;
                    }
                    pageMap[location.key][location.index] = nextItem;
                };

                const removeTopLevelItem = (location) => {
                    if (!location) return null;
                    if (isArrayData) {
                        const [removed] = arrayData.splice(location.index, 1);
                        return removed;
                    }
                    const [removed] = pageMap[location.key].splice(location.index, 1);
                    return removed;
                };

                const prependTopLevelItem = (nextItem) => {
                    if (isArrayData) {
                        arrayData.unshift(nextItem);
                        return;
                    }
                    const firstKey = pageKeys[0] || '1';
                    if (!pageMap[firstKey]) {
                        pageMap[firstKey] = [];
                        if (!pageKeys.includes(firstKey)) pageKeys.unshift(firstKey);
                    }
                    pageMap[firstKey].unshift(nextItem);
                };

                const updateNestedComment = (updater) => {
                    if (isArrayData) {
                        for (let i = 0; i < arrayData.length; i++) {
                            const item = arrayData[i];
                            const nextItem = updater(item);
                            if (nextItem) {
                                arrayData[i] = nextItem;
                                return true;
                            }
                        }
                        return false;
                    }
                    for (let i = 0; i < pageKeys.length; i++) {
                        const key = pageKeys[i];
                        const currentPage = pageMap[key] || [];
                        for (let j = 0; j < currentPage.length; j++) {
                            const nextItem = updater(currentPage[j]);
                            if (nextItem) {
                                currentPage[j] = nextItem;
                                return true;
                            }
                        }
                    }
                    return false;
                };

                // 删除
                if (params.isDeleted) {
                    const location = findTopLevelItem();
                    if (location) {
                        removeTopLevelItem(location);
                    } else {
                        updateNestedComment((item) => {
                            const lastThree = Array.isArray(item.lastThreeComments) ? [...item.lastThreeComments] : [];
                            const commentIndex = lastThree.findIndex(c => (c?.id || c?._id) === id);
                            if (commentIndex === -1) return null;
                            lastThree.splice(commentIndex, 1);
                            return {
                                ...item,
                                lastThreeComments: lastThree,
                                replyCount: Math.max(0, (item.replyCount || 0) - 1),
                            };
                        });
                    }
                }

                // 沉帖操作
                if (typeof params.isSink !== 'undefined') {
                    const location = findTopLevelItem();
                    if (location) {
                        if (params.isSink === true) {
                            const hasMore = !!listRef.hasMore;
                            if (hasMore) {
                                removeTopLevelItem(location);
                            } else {
                                replaceTopLevelItem(location, { ...location.item, isSink: true });
                            }
                        } else {
                            replaceTopLevelItem(location, { ...location.item, isSink: false });
                        }
                    }
                }

                // 置顶操作
                if (typeof params.isSticky !== 'undefined') {
                    const location = findTopLevelItem();
                    if (location) {
                        if (params.isSticky === true) {
                            const removedItem = removeTopLevelItem(location);
                            if (removedItem) {
                                prependTopLevelItem({ ...removedItem, isSticky: true });
                            }
                        } else {
                            replaceTopLevelItem(location, { ...location.item, isSticky: false });
                        }
                    }
                }

                if (isArrayData) {
                    listRef.data = arrayData;
                } else {
                    const nextData = {};
                    pageKeys.forEach(key => {
                        nextData[key] = pageMap[key] || [];
                    });
                    listRef.data = nextData;
                }
                anyNamespaceUpdated = true;
            };

            namespaces.forEach(applyUpdate);
            if (anyNamespaceUpdated) {
                this.listStore.lists = { ...this.listStore.lists };
            }
        });

        return ret;
    }
    
    
    /**
   * 帖子点赞
   * @param {object} parmas * 参数
   * @param {number} parmas.id * 帖子id
   * @param {number} parmas.pid * 帖子评论od
   * @param {boolean} params.isLiked 是否点赞
   * @returns {object} 处理结果
   */
    // @action
    // async updateLiked(params, IndexStore, UserStore) {
    //     const { id, pid, isLiked } = params;
    //     if (!id || !pid) {
    //     return {
    //         msg: '参数不完整',
    //         success: false,
    //     };
    //     }

    //     const requestParams = {
    //     id,
    //     postId: pid,
    //     data: {
    //         attributes: {
    //             isLiked: !!isLiked,
    //         },
    //     },
    //     };
    //     const res = await updatePosts({ data: requestParams });

    //     if (res?.data && res.code === 0) {
    //         if (this.threadData && this.threadData.id == id) {
    //             this.setThreadDetailField('isLiked', !!isLiked);
    //             this.setThreadDetailField('likeCount', res.data.likeCount);

    //             // 更新点赞的用户
    //             // const currentUser = UserStore?.userInfo;
    //             // if (currentUser) {
    //             //     const userData = threadReducer.createUpdateLikeUsersData(currentUser, 1);
    //             //     const newLikeUsers = threadReducer.setThreadDetailLikedUsers(this.threadData?.likeReward, !!isLiked, userData);
    //             //     this.updateLikeReward(newLikeUsers);
    //             // }

    //             // 更新列表store
    //             this.updateListStore();
    //         }

    //         return {
    //             msg: '操作成功',
    //             success: true,
    //         };
    //     }

    //     return {
    //         msg: res.msg,
    //         success: false,
    //         res:res
    //     };
    // }

    /**
   * 更新列表页store
   */
    // @action
    // async updateListStore() {
    //     const id = this.threadData?.threadId || this.threadData?.id;

    //     if (id) {
    //         this.threadList.updateAssignThreadInfoInLists({
    //             threadId: id,
    //             threadInfo: this.threadData,
    //         });
    //     }
    // }

    /**
     * 获取楼中楼评论列表
     * @param {number} postId 评论ID
     * @param {object} params 参数（filter[idLt], page[size]等）
     * @returns {Promise}
     */
    @action
    async getPostComments(postId, params = {}) {
        try {
            const res = await readPostComments(postId, params);
            const code = typeof res.code !== 'undefined' ? res.code : res.Code;
            if (code === 0) {
                return {
                    success: true,
                    data: res.data || res.Data || {},
                };
            } else {
                Toast.error(res.message || res.Message || '加载失败');
                return { success: false };
            }
        } catch (error) {
            console.error('获取楼中楼评论失败:', error);
            Toast.error('加载失败');
            return { success: false };
        }
    }

    /**
     * 设置 threadData
     * @param {array} id 帖子列表
     */
    @action
    setThreadData(data) {
        // 如果传入 null 或 undefined，则清空 threadData
        if (data == null) {
            this.threadData = null;
            return;
        }
        this.threadData = { ...this.threadData, ...data };
        if (typeof data.id !== 'undefined') {
            this.threadData.id = data.id;
        }
    }

    /**
     * 清空 threadData（用于进入详情页前重置数据）
     */
    @action
    clearThreadData() {
        this.threadData = null;
    }

    /**
     * 获取 MobX 响应式对象的 id 字段（兼容 Proxy）
     * @param {object} data 帖子或评论对象
     * @returns {string|number|undefined} id
     */
    getId(data) {
        if (!data) return undefined;
        // 兼容 MobX Proxy、普通对象
        if (typeof data.id !== 'undefined') return data.id;
        if (typeof data.get === 'function') return data.get('id');
        // 兜底遍历属性
        for (const key in data) {
            if (key === 'id') return data[key];
        }
        return undefined;
    }

    /**
     * 获取反馈列表（异步）
     * 统一管理 loading、错误、分页、hasMore 等状态
     * 页面和 list 组件可直接响应 listStore 的状态
     * @param {object} params 查询参数
     *   - params.page[number]: 页码
     *   - params.page[size]: 每页数量
     *   - params.filter: 过滤条件（可选）
     * @param {string} [namespace='feedbackList'] listStore 命名空间
     * @returns {Promise<void>}
     */
    @action
    async getFeedbackList(params, namespace = 'feedbackList') {
        runInAction(() => {
            this.listStore.setAttribute({ namespace, key: 'loading', value: true });
        });
        const res = await readFeedbacks(params);
        runInAction(() => {
            console.log('Activity list response:', res, 'Params:', params);
            if (res.code === 0) {
                // 适配新格式：数据在 res.data.data，分页在 res.data.page
                const threadData = res.data && res.data.data ? res.data.data : res.data;
                const pageInfo = res.data && res.data.page ? res.data.page : {};
                
                this.listStore.setList({
                    namespace,
                    data: { data: threadData },
                    path: 'data',
                    page: params.page || params['page[number]'] || pageInfo.number || 1,
                    search: { filter: params.filter || {} },
                    hasMore: pageInfo.hasMore !== undefined ? pageInfo.hasMore : false,
                });
                if (pageInfo.total != null) {
                    this.listStore.setAttribute({
                        namespace,
                        key: 'totalCount',
                        value: pageInfo.total,
                    });
                }
                this.listStore.setAttribute({ namespace, key: 'requestError', value: { isError: false, errorText: '' } });
            } else {
                this.listStore.setAttribute({ namespace, key: 'requestError', value: { isError: true, errorText: res.message || '加载失败' } });
            }
            this.listStore.setAttribute({ namespace, key: 'loading', value: false });
        });
        // console.log('这是列表呀', res);
        return res
    }

    /**
     * 获取问题反馈-问题类型列表
     */
    async getFeedbackProblems(params = {}) {
        const res = await readFeedbackProblems(params);
        return res;
    }

    /**
     * 获取已解决问题数量
     */
    async getFeedbackSolvedCount(params = {}) {
        const res = await readFeedbackSolvedCount(params);
        return res;
    }

    /**
     * 获取问题反馈-手机型号分类列表
     */
    async getFeedbackProducts(params = {}) {
        const res = await readProductCategories(params);
        return res;
    }

    /**
     * 获取产品列表（按分类筛选）
     */
    async getProducts(params = {}) {
        const requestParams = {
            'page[number]': params['page[number]'] ?? 1,
            'page[size]': params['page[size]'] ?? 100,
        };

        const categoryId = params['filter[categoryId]'] ?? params.categoryId;
        if (categoryId !== undefined && categoryId !== '') {
            requestParams['filter[categoryId]'] = Number(categoryId);
        }

        const keywords = params['filter[keywords]'] ?? params.keywords;
        if (keywords && String(keywords).trim()) {
            requestParams['filter[keywords]'] = String(keywords).trim();
        }

        const res = await readProducts(requestParams);
        return res;
    }

    /**
     * 用户确认有无帮助
     */
    async submitFeedbackConfirmData(id, params = {}) {
        const res = await submitFeedbackConfirm(id, params);
        return res;
    }

    /**
     * 直接设置 feedbackList（通常不推荐，建议用 listStore 统一管理）
     * @param {array} feedbackList 帖子列表
     */
    @action
    setFeedbackList(feedbackList) {
        this.feedbackList = feedbackList;
    }
}


// 单例导出，方便全局调用
const threadAction = new ThreadAction();
export default threadAction;
